import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Auth } from '@aws-amplify/auth';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Switch,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
} from '@material-ui/core';
import { isEmpty, get, every, find, orderBy, flatMap } from 'lodash';
import moment from 'moment';
import { line, area, curveStepAfter } from 'd3-shape';
// import CloseIcon from '@material-ui/icons/Close';
import {
  Chart,
  ArgumentAxis,
  AreaSeries,
  LineSeries,
  Tooltip,
} from '@devexpress/dx-react-chart-bootstrap4';
import { Animation, EventTracker } from '@devexpress/dx-react-chart';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { RootState } from 'redux-modules/store/rootState';
import {
  Box,
  Dialog,
  Button,
  StyledTableRow,
  StyledTableCell,
} from 'components';
import {
  welcomeInvestorImg,
  TransactionImg,
  PortfolioAnalyticsImg,
  PortfolioSummaryImg,
} from 'assets';
import { getInvestorDetails } from 'redux-modules/investor/Actions';
import { handleSignOut } from 'redux-modules/auth/Actions';
import history from 'common/utils/history';
import {
  errorMessageHandler,
  formatAmount,
  formatMoney,
  sortByDate,
  // getNavTotal,
  convertAmountIntoReadableFormat,
} from 'common/utils/helpers';
import {
  getInvestorPortfolio,
  getInvestorNavTrack,
  getInvestorPayments,
  getInvestorAggregatePortfolio,
  getUpcomingDistributions,
  getDealToVisit,
} from 'services';
import { getInvestorAllDeal } from 'redux-modules/deals/Actions';

import { TermsAndPrivacy, CompleteProfile } from './components';
import styles from './styles';
import PendingInvestment from './components/PendingInvestment';

const format = () => (tick: any) => tick;

const Area = (props: any) => (
  <AreaSeries.Path
    {...props}
    path={area()
      .x(({ arg }: any) => arg)
      .y1(({ val }: any) => val)
      .y0(({ startVal }: any) => startVal)
      .curve(curveStepAfter)}
  />
);

const Line = (props: any) => (
  <LineSeries.Path
    {...props}
    path={line()
      .x(({ arg }: any) => arg)
      .y(({ val }: any) => val)
      .curve(curveStepAfter)}
  />
);

const TooltipContent = (props: any) => (
  <Tooltip.Content {...props} text={`${formatAmount(props.text)}`} />
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const classes = styles();
  const componentMounted = useRef(true);

  const { name } = useSelector(({ Auth }: RootState) => Auth.user);
  const { investor, isLoading, errorMessage, investorDeals } = useSelector(
    ({ Investor }: RootState) => Investor,
  );
  const { dealList, latestDeal } = useSelector(({ Deals }: RootState) => Deals);

  const [agreeTermsLoading, setAgreeTermsLoading] = React.useState<boolean>(
    false,
  );
  const [modal, setModal] = React.useState<boolean>(false);
  const [
    completeProfileModal,
    setCompleteProfileModal,
  ] = React.useState<boolean>(false);
  const [termsPrivacyModal, setTermsPrivacyModal] = React.useState<boolean>(
    false,
  );
  // eslint-disable-next-line no-unused-vars
  const [termsAccepted, setTermsAccepted] = React.useState<boolean>(false);
  const [selectedNav, setSelectedNav] = React.useState(false);
  const [portfolioCardData, setPortfolioCardData] = React.useState<any>({});
  const [navTrackData, setNavTrackData] = React.useState<any>([]);
  const [paymentsList, setPaymentsList] = React.useState<any>([]);
  const [chartData, setChartData] = useState<any>([]);
  const [targetItem, setTargetItem] = React.useState<any>();
  const [upcomingDistribution, setUpcomingDistribution] = React.useState<any>();
  const [newLatestDeal, setNewLatestDeal] = React.useState<any>([]);

  useEffect(() => {
    const getUserAuthData = async () => {
      const userData = await Auth.currentAuthenticatedUser();
      if (userData.attributes['custom:signin_first_time'] !== '1') {
        setModal(true);
        setTermsAccepted(false);
      } else {
        if (componentMounted.current) setTermsAccepted(true);
      }
    };
    if (get(investor, 'id')) getUserAuthData();

    return () => {
      componentMounted.current = false;
    };
  }, [investor]);

  // TODO: Refactor (AuthWrapper.tsx) this flow api calling multiple time
  useEffect(() => {
    if (isEmpty(get(investor, 'id'))) getInvestorProfileDetail();
    if (isEmpty(dealList)) dispatch(getInvestorAllDeal());
  }, []);

  useEffect(() => {
    if (!isEmpty(get(investor, 'id')))
      Promise.all([
        getInvestorPaymentsData(),
        getInvestorAggregatePortfolioData(),
      ]);
  }, [investor]);

  useEffect(() => {
    if (
      !isEmpty(get(investor, 'id')) &&
      !isEmpty(investorDeals) &&
      investor.reviewed
    ) {
      const portfolioPromise = investorDeals.map((spv: string) =>
        getInvestorPortfolio(spv),
      );
      Promise.all([getInvestorNavTrack(), ...portfolioPromise])
        .then(([trackData, ...portfolioPromiseData]: any) => {
          setNavTrackData(trackData);
          setNavChartAllData(trackData, portfolioPromiseData);
        })
        .catch((err: any) => {
          // eslint-disable-next-line no-unused-vars
          const message: string = errorMessageHandler(err);
          setNavTrackData([]);
        });
    }
  }, [investor, investorDeals]);

  useEffect(() => {
    if (investor.reviewed && !isEmpty(investorDeals)) {
      getUpcomingDistributions()
        .then((res: any) => {
          if (!isEmpty(get(res, 'spv')) && get(res, 'balance') > 0) {
            setUpcomingDistribution(res);
          }
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          // eslint-disable-next-line no-console
          console.log('ERROR WHILE FETCHING UPCOMING DISTRIBUTION: ', message);
        });
    }
  }, [investor, investorDeals]);

  useEffect(() => {
    if (!isEmpty(dealList) && !isEmpty(investorDeals)) {
      const filteredDeal = dealList.filter((deal: any) => {
        return !investorDeals.includes(get(deal, 'id')) && !deal.closed;
      });
      const sortedDeal = orderBy(filteredDeal, ['order'], ['asc']);
      setNewLatestDeal(sortedDeal.slice(0, 2));
    } else {
      const filteredDeal = dealList.filter((deal: any) => !deal.closed);
      const sortedDeal = orderBy(filteredDeal, ['order'], ['asc']);
      setNewLatestDeal(sortedDeal.slice(0, 2));
    }
  }, [dealList, investorDeals]);

  const getInvestorProfileDetail = () => {
    dispatch(getInvestorDetails());
  };

  const handleAgreeTermsAndContinue = async () => {
    setAgreeTermsLoading(true);
    // if (termsAccepted) {
    const userData = await Auth.currentAuthenticatedUser();
    if (userData.attributes['custom:signin_first_time'] !== '1') {
      await Auth.updateUserAttributes(userData, {
        'custom:signin_first_time': (1).toString(),
      });
      if (userData.attributes['custom:referred'] === '1') {
        await getDealToVisit()
          .then((res: any) => {
            if (!isEmpty(res)) {
              history.push({
                pathname: `deals`,
                search: `?spv=${res}`,
                state: {
                  dealVisited: true,
                },
              });
            }
          })
          .catch((err: any) => {
            // eslint-disable-next-line no-console
            console.log('ERROR WHILE FETCHING DEAL TO VISIT: ', err);
          });
      }
    }
    setModal(false);
    setAgreeTermsLoading(false);
    // } else {
    //   setModal(false);
    //   setAgreeTermsLoading(false);
    // }
  };

  const getInvestorAggregatePortfolioData = () => {
    if (isEmpty(portfolioCardData) && investor.reviewed) {
      getInvestorAggregatePortfolio()
        .then((res: any) => {
          setPortfolioCardData(res);
        })
        .catch((err: any) => {
          // eslint-disable-next-line no-unused-vars
          const message: string = errorMessageHandler(err);
        });
    }
  };

  const getInvestorPaymentsData = () => {
    if (isEmpty(paymentsList) && investor.reviewed) {
      getInvestorPayments()
        .then((res: any) => {
          setPaymentsList(
            orderBy(res, ['investmentDetail.investmentDate'], ['desc']),
          );
        })
        .catch((err: any) => {
          // eslint-disable-next-line no-unused-vars
          const message: string = errorMessageHandler(err);
        });
    }
  };

  const handleSwitchChange = (event: any) => {
    setSelectedNav(!event.target.checked);
  };

  const getNavChartData = (
    navTrackDataV2: any,
    portfolioCardDataV2: any,
    addTodayDate = true,
  ) => {
    if (!isEmpty(navTrackDataV2)) {
      const sorted = navTrackDataV2.sort(sortByDate);
      const navValueBeforeInvest = sorted
        .slice()
        .reverse()
        .find((navTrack: any) => {
          if (
            moment(get(navTrack, 'date')).isBefore(
              get(portfolioCardDataV2, 'investmentDate'),
            )
          )
            return navTrack;
        });
      const newSortedTrack = sorted.filter((navTrack: any) => {
        if (
          !moment(get(navTrack, 'date')).isBefore(
            get(portfolioCardDataV2, 'investmentDate'),
          )
        )
          return navTrack;
      });
      const lastIndex = newSortedTrack.length - 1;
      const data = newSortedTrack.map((item: any) => {
        return {
          month: moment(get(item, 'date')).format('DD MMM YY'),
          nav: parseFloat(get(item, 'nav', 0)),
          tv:
            parseFloat(get(item, 'nav', 0)) +
            parseFloat(get(item, 'distribution', 0)),
        };
      });
      data.unshift.apply(data, [
        {
          month: moment(get(portfolioCardDataV2, 'investmentDate')).format(
            'DD MMM YY',
          ),
          nav: parseFloat(
            get(navValueBeforeInvest, 'nav') ||
              get(portfolioCardDataV2, 'totalInvestments', 0),
          ),
          tv:
            parseFloat(
              get(navValueBeforeInvest, 'nav') ||
                get(portfolioCardDataV2, 'totalInvestments', 0),
            ) + parseFloat(get(navValueBeforeInvest, 'distribution') || 0),
        },
      ]);
      addTodayDate &&
        data.push({
          month: moment().format('DD MMM YY'),
          nav: parseFloat(
            get(newSortedTrack, `[${lastIndex}].nav`) ||
              get(navValueBeforeInvest, 'nav') ||
              get(portfolioCardDataV2, 'totalAssetValue', 0),
          ),
          tv:
            parseFloat(
              get(newSortedTrack, `[${lastIndex}].nav`) ||
                get(navValueBeforeInvest, 'nav') ||
                get(portfolioCardDataV2, 'totalAssetValue', 0),
            ) +
            parseFloat(
              get(newSortedTrack, `[${lastIndex}].distribution`) ||
                get(navValueBeforeInvest, 'distribution') ||
                get(portfolioCardDataV2, 'comulativeDistribution', 0),
            ),
        });
      return data;
    } else {
      return [
        ...(addTodayDate
          ? [
              {
                month: moment(
                  get(portfolioCardDataV2, 'investmentDate'),
                ).format('DD MMM YY'),
                nav: parseFloat(
                  get(portfolioCardDataV2, 'totalInvestments', 0),
                ),
                tv: parseFloat(get(portfolioCardDataV2, 'totalInvestments', 0)),
              },
              {
                month: moment().format('DD MMM YY'),
                nav: parseFloat(get(portfolioCardDataV2, 'totalAssetValue', 0)),
                tv:
                  parseFloat(get(portfolioCardDataV2, 'totalAssetValue', 0)) +
                  parseFloat(
                    get(portfolioCardDataV2, 'comulativeDistribution', 0),
                  ),
              },
            ]
          : [
              {
                month: moment(
                  get(portfolioCardDataV2, 'investmentDate'),
                ).format('DD MMM YY'),
                nav: parseFloat(
                  get(portfolioCardDataV2, 'totalInvestments', 0),
                ),
                tv: parseFloat(get(portfolioCardDataV2, 'totalInvestments', 0)),
              },
            ]),
      ];
    }
  };

  const setNavChartAllData = (
    navTrackDataV2: any,
    portfolioCardDataV2: any,
  ) => {
    setChartData([]);
    const orderByportfolioCardData = orderBy(
      portfolioCardDataV2,
      'investmentDate',
      'asc',
    );
    const allSpvChartData = orderByportfolioCardData.map(
      (portfolioData: any, index: number) =>
        getNavChartData(
          navTrackDataV2.filter(
            (navTrack: any) => navTrack.spv === portfolioData.spv,
          ),
          portfolioData,
          index === orderByportfolioCardData.length - 1,
        ),
    );
    const allSpvChartNewData = JSON.parse(JSON.stringify(allSpvChartData));
    allSpvChartNewData.map((spvChartData: any, index: number) => {
      if (index) {
        const prevSpvData = allSpvChartNewData[index - 1];
        const prevSpvLastData = prevSpvData[prevSpvData.length - 1];
        return spvChartData.map((chartData: any) => {
          chartData.nav = chartData.nav + get(prevSpvLastData, 'nav', 0);
          chartData.tv = chartData.tv + get(prevSpvLastData, 'tv', 0);
          return chartData;
        });
      } else return spvChartData;
    });
    setChartData(flatMap(allSpvChartNewData));
  };

  const welcomeInvestorBlock = () => {
    return (
      <Box className={classes.welcomeLtrxn}>
        <Box className={classes.lTrxnCont}>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.trxnHText}
          >
            Investment Opportunities
          </Typography>
          <Typography variant="h3" gutterBottom className={classes.trxnHstext}>
            Welcome, {name}!
          </Typography>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.trxnCText}
          >
            <b>8VDX {get(latestDeal, 'deal')} SPV</b> is open for investments.
          </Typography>
          {get(investor, 'reviewed') ? (
            <>
              <Typography
                variant="body1"
                gutterBottom
                className={classes.helpUText}
              >
                Watch out this space and your inbox for future investment
                opportunities.
              </Typography>
              <Button
                name="Read More and Invest"
                className={classes.trxnButton}
                onClick={() => history.push('/deals')}
              />
            </>
          ) : every(
              [
                isEmpty(investor.linkedinUrl),
                isEmpty(investor.twitterUrl),
                isEmpty(investor.anglelistUrl),
                isEmpty(investor.crunchbaseUrl),
                isEmpty(investor.otherUrl),
              ],
              Boolean,
            ) ? (
            <>
              <Typography
                variant="body1"
                gutterBottom
                className={classes.helpUText}
              >
                You'll receive full access to the portal after successful
                verification of your credentials. You'll be notified through an
                email.
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                className={classes.helpUText}
              >
                Help us expediate verification by providing links to your
                profiles on <b>Angelist, Linkedin,</b> etc.
              </Typography>
              <Button
                name="Add social media profile"
                className={classes.trxnButton}
                onClick={() => setCompleteProfileModal(true)}
              />
            </>
          ) : !investor.reviewed ? (
            <>
              <Typography
                variant="body1"
                gutterBottom
                className={classes.helpUText}
              >
                You'll receive full access to the portal after successful
                verification of your credentials. You'll be notified through an
                email.
              </Typography>
              <Button
                name="Get Started"
                className={classes.trxnButton}
                disabled
              />
            </>
          ) : (
            <>
              <Typography
                variant="body1"
                gutterBottom
                className={classes.helpUText}
              >
                Watch out this space and your inbox for future investment
                opportunities.
              </Typography>
              <Button
                name="Read More and Invest"
                className={classes.trxnButton}
                onClick={() => history.push('/deals')}
              />
            </>
          )}
        </Box>
      </Box>
    );
  };

  const transactionBlock = () => {
    return (
      <Box
        className={classes.welcomeRtrxn}
        onClick={() => investor.reviewed && history.push('/transactions')}
        style={{
          cursor: investor.reviewed ? 'pointer' : 'default',
        }}
      >
        <Box className={classes.rTrxnCont}>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.trxnHText}
          >
            Transactions
          </Typography>
          {!isEmpty(paymentsList) && investor.reviewed ? (
            <>
              <Typography
                variant="body1"
                gutterBottom
                className={classes.helpUText}
              >
                Your recent transactions on 8vdX
              </Typography>
              <Table>
                <TableBody>
                  {paymentsList.map((row: any) => (
                    <StyledTableRow
                      hover
                      key={row.id}
                      className={classes.tableRow}
                    >
                      <StyledTableCell className={classes.tableCell}>
                        {' '}
                        {moment(
                          get(row, 'investmentDetail.investmentDate'),
                        ).format('MMM DD')}
                      </StyledTableCell>
                      <StyledTableCell className={classes.tableCell}>
                        SPV {get(find(dealList, { id: row?.spv }), 'deal')}
                      </StyledTableCell>
                      <StyledTableCell className={classes.tableCell}>
                        {' '}
                        <Typography
                          className={classes.tableAmonut}
                          variant="caption"
                        >
                          {formatAmount(row?.amount)}
                        </Typography>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
              <Box className={classes.viewMoreBox}>
                <Typography className={classes.viewMoreTxt}>
                  View More <ChevronRightIcon className={classes.rightIcon} />
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <img
                src={TransactionImg}
                alt={TransactionImg}
                className={classes.trxnsImage}
              />
              <Typography
                variant="body1"
                gutterBottom
                className={classes.helpUText}
                align="center"
              >
                Your recent transactions will appear here once you start
                investing.
              </Typography>
            </>
          )}
        </Box>
      </Box>
    );
  };

  const portfolioBlock = () => {
    return (
      <Box
        className={classes.protLtrxn}
        onClick={() => investor.reviewed && history.push('/portfolio')}
        style={{
          cursor: investor.reviewed ? 'pointer' : 'default',
        }}
      >
        <Box className={classes.rTrxnCont}>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.trxnHText}
          >
            Portfolio Summary
          </Typography>
          {!isEmpty(paymentsList) && investor.reviewed ? (
            <>
              <Typography
                variant="body1"
                gutterBottom
                className={classes.helpUText}
              >
                Your company’s Portfolio summary
              </Typography>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <StyledTableCell className={classes.tableHeadCell}>
                      SPV
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.tableHeadCell}
                      style={{
                        textAlign: 'center',
                      }}
                    >
                      Investment{' '}
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.tableHeadCell}
                      style={{
                        textAlign: 'center',
                      }}
                    >
                      Total
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentsList.map((row: any) => (
                    <StyledTableRow
                      hover
                      key={row.id}
                      className={classes.tableRow}
                    >
                      <StyledTableCell className={classes.tableCell}>
                        {get(find(dealList, { id: row?.spv }), 'deal')}
                      </StyledTableCell>
                      <StyledTableCell
                        className={classes.tableCell}
                        style={{
                          textAlign: 'center',
                        }}
                      >
                        {' '}
                        <Typography
                          className={classes.tableAmonut}
                          variant="caption"
                        >
                          {formatAmount(get(row, 'amount', 0))}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell
                        className={classes.tableCell}
                        style={{
                          textAlign: 'center',
                        }}
                      >
                        {' '}
                        <Typography
                          className={classes.tableTotalAmonut}
                          variant="caption"
                        >
                          {formatAmount(
                            parseFloat(
                              get(row, 'portfolio.totalAssetValue', 0),
                            ) +
                              parseFloat(
                                get(row, 'portfolio.comulativeDistribution', 0),
                              ),
                          )}
                        </Typography>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
              <Box className={classes.viewMoreBox}>
                <Typography className={classes.viewMoreTxt}>
                  View More <ChevronRightIcon className={classes.rightIcon} />
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <img
                src={PortfolioSummaryImg}
                alt={PortfolioSummaryImg}
                className={classes.trxnsImage}
              />
              <Typography
                variant="body1"
                gutterBottom
                align="center"
                className={classes.helpUText}
              >
                Summary of your outstanding investments will appear here.
              </Typography>
            </>
          )}
        </Box>
      </Box>
    );
  };

  const portfolioAnalyticBlock = () => {
    return (
      <Box
        className={classes.protRtrxn}
        style={{
          cursor: investor.reviewed ? 'pointer' : 'default',
        }}
      >
        <Box className={classes.lProtCont}>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.trxnHText}
            style={{
              marginBottom: 10,
            }}
          >
            Portfolio Analytics
          </Typography>
          {(!isEmpty(paymentsList) || !isEmpty(navTrackData)) &&
          investor.reviewed ? (
            <>
              <Box>
                <Box className={classes.graphHeader}>
                  <Switch
                    classes={{
                      root: classes.switchRoot,
                      switchBase: classes.switchBase,
                      thumb: classes.thumb,
                      track: classes.track,
                      checked: classes.checked,
                    }}
                    checked={!selectedNav}
                    onChange={handleSwitchChange}
                    name="areaValueType"
                    inputProps={{ 'aria-label': 'secondary checkbox' }}
                  />
                  {/* <Typography
                    variant="caption"
                    className={classes.spvLabelText}
                  >
                    SPV{' '}
                    {get(
                      find(dealList, { id: get(latestDealInvestor, 'spv') }),
                      'deal',
                    )}
                  </Typography> */}
                </Box>
                <Box onClick={() => history.push('/portfolio')}>
                  <Typography className={classes.graphPHText}>
                    <Typography
                      variant="caption"
                      className={classes.gDollorText}
                    >
                      $
                    </Typography>
                    {/* {formatMoney(
                      getNavTotal('', portfolioCardData, selectedNav) || 0,
                    )} */}
                    {formatMoney(
                      (selectedNav
                        ? get(chartData, `${[chartData.length - 1]}.nav`)
                        : get(chartData, `${[chartData.length - 1]}.tv`)) || 0,
                    )}
                  </Typography>
                  <Typography className={classes.graphPSText}>
                    As of {moment().format('DD MMM, YY')}
                  </Typography>
                  <Box className={classes.chatGraphBox}>
                    {!isEmpty(chartData) && (
                      <Chart data={chartData} height={133}>
                        <ArgumentAxis tickFormat={format} />
                        <LineSeries
                          name="NavLine"
                          valueField={selectedNav ? 'nav' : 'tv'}
                          argumentField="month"
                          color="#322EDC"
                          seriesComponent={Line}
                        />
                        <AreaSeries
                          name="NavArea"
                          valueField={selectedNav ? 'nav' : 'tv'}
                          argumentField="month"
                          color="#322EDC"
                          seriesComponent={Area}
                        />
                        <Animation />
                        <EventTracker />
                        <Tooltip
                          targetItem={targetItem}
                          onTargetItemChange={(targetItem: any) =>
                            setTargetItem(targetItem)
                          }
                          contentComponent={TooltipContent}
                        />
                      </Chart>
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <img
                src={PortfolioAnalyticsImg}
                alt={PortfolioAnalyticsImg}
                className={classes.trxnsImage}
              />
              <Typography
                variant="body1"
                gutterBottom
                className={classes.helpUText}
                align="center"
              >
                Changes in your portfolio value will appear here. We will
                display charts showing NAV, Total Value and Transactions.
              </Typography>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return isLoading ? (
    <Box className={classes.loader}>
      <CircularProgress color="inherit" size={40} />
    </Box>
  ) : (
    <Box className={classes.dashboardBox}>
      {/* eslint-disable-next-line */}
      {console.log('LOADED')}
      {errorMessage && (
        <Typography variant="body1" className={classes.errorMessage}>
          {errorMessage}
        </Typography>
      )}
      {!isEmpty(upcomingDistribution) && (
        <Box
          className={classes.notificationBox}
          onClick={() =>
            history.push(
              `/portfolio?spv=${get(upcomingDistribution, 'spv.deal')}`,
            )
          }
        >
          {/* <Typography className={classes.notificationTxt}>
            There is an upcoming distribution of
            {formatAmount(get(upcomingDistribution, 'balance', 0), {
              maximumFractionDigits: 2,
            })}{' '}
            from your investment in {get(upcomingDistribution, 'spv.deal')} SPV.
            Action required before{' '}
            {moment(get(upcomingDistribution, 'spv.closedAt'))
              .add(15, 'd')
              .format('DD MMMM, YYYY')}
            .
          </Typography> */}
          <Typography className={classes.notificationTxt}>
            There are outstanding actions related to your roll-over amounts from
            SPVs for Bridge to YC Demo Day. Please click here to review and
            rollover into Demo Day and/or Continuity funds.
          </Typography>
          {/* <CloseIcon className={classes.closeIcon} /> */}
        </Box>
      )}
      <PendingInvestment />
      {!isEmpty(newLatestDeal) && (
        <Accordion className={classes.accordionBox} square={true}>
          <AccordionSummary
            expandIcon={<ChevronRightIcon fontSize="large" />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography className={classes.accordionHeading}>
              Explore New Deals
            </Typography>
            <Button
              name="See More"
              className={classes.seeMoreDealButton}
              onClick={() => history.push('/deals')}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Box className={classes.newDealBox}>
              {newLatestDeal.map((deal: any) => (
                <Box className={classes.invstBulkList} key={get(deal, 'id')}>
                  {newLatestDeal.some(
                    (nDeal: any) => get(nDeal, 'id') === get(deal, 'id'),
                  ) && (
                    <Typography className={classes.newDeals}>
                      New Deal
                    </Typography>
                  )}
                  <Box className={classes.invstNameBox}>
                    <Box className={classes.invstNameLeft}>
                      <Typography className={classes.nameTxt}>
                        {get(deal, 'fundTitle')} SPV
                      </Typography>
                    </Box>
                    <Box className={classes.invstNameRight}>
                      <Typography className={classes.amountTxt}>
                        $
                        {convertAmountIntoReadableFormat(get(deal, 'dealSize'))}
                      </Typography>
                      {!get(deal, 'closed') && (
                        <Typography className={classes.completeTxt}>
                          {get(deal, 'completedPercentage', 0)}% Funded
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      {!isEmpty(get(investor, 'id')) && (
        <>
          <Box className={classes.welcomeContainer}>
            <Box className={classes.welcomeTrxn}>
              {welcomeInvestorBlock()}
              {transactionBlock()}
            </Box>
            <Box className={classes.protOther}>
              {portfolioBlock()}
              {portfolioAnalyticBlock()}
            </Box>
          </Box>
        </>
      )}

      <Dialog
        open={modal}
        maxWidth={'sm'}
        title="Welcome to 8vdX Investor Portal"
        buttonText="Continue"
        handleClose={(_: any, reason: any) => {
          if (reason && reason === 'backdropClick') return;
          dispatch(handleSignOut());
          setModal(false);
        }}
        disableEscapeKeyDown
      >
        <Box>
          <Box className={classes.welcomeBoxBody}>
            <img
              src={welcomeInvestorImg}
              alt={welcomeInvestorImg}
              className={classes.welcomeImgImage}
            />
          </Box>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.welcomeModalHeaderText}
          >
            Confidentiality Reminder
          </Typography>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.welcomeModalText}
          >
            Keep the information about Investment opportunities,
            target/portfolio companies and their valuations confidential.
            Violating confidentiality can harm your portfolio and you will no
            longer be able to invest through 8vdX.
          </Typography>
          {/* <Box className={classes.checkBoxAgree}>
            <Checkbox
              style={{ padding: '0 9px 0 0' }}
              checked={termsAccepted}
              onChange={(e: any) => {
                setTermsAccepted(e.target.checked);
              }}
            />
            <Typography variant="caption" className={classes.agreeTerms}>
              I agree to the
              <a
                onClick={() => setTermsPrivacyModal(true)}
                rel="noreferrer"
                className={classes.termAndConditionLink}
              >
                {' '}
                terms of services
              </a>{' '}
              and
              <a
                onClick={() => setTermsPrivacyModal(true)}
                rel="noreferrer"
                className={classes.termAndConditionLink}
              >
                {' '}
                privacy policy.
              </a>
            </Typography>
          </Box> */}
          <Box className={classes.continueAndLogoutButtonBox}>
            <Button
              className={classes.continueBtn}
              name="Agree and Continue"
              onClick={() => {
                setTermsAccepted(true);
                handleAgreeTermsAndContinue();
              }}
              isLoading={agreeTermsLoading}
              disabled={agreeTermsLoading}
            />
            <Button
              className={classes.logoutBtn}
              name="Logout"
              onClick={() => {
                dispatch(handleSignOut());
                setModal(false);
              }}
              disabled={agreeTermsLoading}
            />
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={termsPrivacyModal}
        maxWidth={'md'}
        title="Terms of Services"
        buttonText="Continue"
        handleClose={() => setTermsPrivacyModal(false)}
      >
        <Box>
          <TermsAndPrivacy />
          <Button
            className={classes.continueBtnBox}
            name="Agree and Continue"
            onClick={() => {
              setTermsAccepted(true);
              setTermsPrivacyModal(false);
            }}
            // isLoading={agreeTermsLoading}
            // disabled={agreeTermsLoading}
          />
        </Box>
      </Dialog>

      <Dialog
        open={completeProfileModal}
        maxWidth={'sm'}
        title="Add social media profile links"
        buttonText="Submit"
        handleClose={() => setCompleteProfileModal(false)}
      >
        <CompleteProfile
          setCompleteProfileModal={setCompleteProfileModal}
          getInvestorProfileDetail={getInvestorProfileDetail}
        />
      </Dialog>
    </Box>
  );
};
export default Dashboard;
